import React, { useEffect, useState } from 'react';
import Navbar from "../components/Navbar";
import Footer from '../components/Footer';
import PageHeader from "../components/PageHeader";
import BackToTop from '../components/BackToTop';
import '../css/gallery.css';
import '../css/mediaPage.css';
import img1 from '../assets/background-4.jpg';
import img2 from '../assets/background-4.jpg';
import img3 from '../assets/background-4.jpg';
import mediaBg from '../assets/background-4.jpg';
import { getVideos } from '../utils/videoStorage';

// Default Data (Fallback)
const DEFAULT_FEATURED = {
    title: "The Rise of D.R.Y: A Journey of Sovereignty",
    desc: "An exclusive documentary covering the historic events that led to the formation of the Democratic Republic of the Yoruba. Witness the passion, the struggle, and the victory.",
    thumbnail: img3,
    duration: "45:00",
    views: "1.5M views"
};

const DEFAULT_VIDEOS = [];

const mergeVideoLists = (stored) => {
    const base = [...DEFAULT_VIDEOS];
    const extras = Array.isArray(stored) ? stored : [];
    extras.forEach((item) => {
        if (!base.find((v) => String(v.id) === String(item.id))) {
            base.push(item);
        }
    });
    return base;
};

export default function VideoPage() {
    const [featuredVideo, setFeaturedVideo] = useState(DEFAULT_FEATURED);
    const [videos, setVideos] = useState(DEFAULT_VIDEOS);
    const [playingVideoId, setPlayingVideoId] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                // Fetch videos directly from API instead of helper to debug path issues
                const RAW_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;
                const API_BASE_URL = RAW_API_BASE_URL.includes('/api') 
                    ? RAW_API_BASE_URL.replace(/\/+$/, '') 
                    : `${RAW_API_BASE_URL.replace(/\/+$/, '')}/api`;

                const response = await fetch(`${API_BASE_URL}/videos`);
                const data = await response.json();
                
                // Process videos to ensure correct URLs
                const processed = data.map(v => {
                    let src = v.videoSource;
                    if (src && !src.startsWith('http')) {
                        // If relative path, prepend server URL
                        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '');
                        src = `${baseUrl}${src.startsWith('/') ? '' : '/'}${src}`;
                    }
                    return { ...v, videoSource: src };
                });

                const merged = mergeVideoLists(processed);
                if (merged.length) {
                    setVideos(merged);
                    setFeaturedVideo(merged[0]);
                } else {
                    setVideos(DEFAULT_VIDEOS);
                    setFeaturedVideo(DEFAULT_FEATURED);
                }
            } catch (err) {
                console.error('Failed to load videos', err);
                setVideos(DEFAULT_VIDEOS);
                setFeaturedVideo(DEFAULT_FEATURED);
            }
        };
        load();
    }, []);

    const handlePlayClick = (video) => {
        setPlayingVideoId(video.id);
        
        // Ensure we have a valid source
        if (video.videoSource) {
            let src = video.videoSource;
            // Add timestamp to prevent caching issues if needed
            if (src.includes('?')) {
                src += `&t=${Date.now()}`;
            } else {
                src += `?t=${Date.now()}`;
            }
            setVideoUrl(src);
        } else if (video.videoFile) {
            // Handle case where videoFile object is present but not source URL
            // This shouldn't happen with API response but good fallback
            setVideoUrl(null);
        } else {
            // Fallback
            setVideoUrl(null);
        }
    };

    return (
        <div className="media-page-body">
            <Navbar />
            <PageHeader title="Video Gallery" path="/video" name="Video" bgImage={mediaBg} />
            
            {/* Cinematic Hero Header */}
            <div className="media-hero" style={{backgroundImage: `url(${mediaBg})`}}>
                <div className="container media-hero-content">
                    <h1>Visual Odyssey</h1>
                    <p>Experience the culture, progress, and stories of our nation in high definition.</p>
                </div>
            </div>

            <div className="container-xxl py-5">
                <div className="container">
                    
                    {/* Trending / Featured Video */}
                    <div className="trending-video-container wow zoomIn" data-wow-delay="0.1s">
                        <img src={featuredVideo.thumbnail} alt="Featured" style={{width: '100%', height: '500px', objectFit: 'cover'}} />
                        <div className="play-overlay">
                             <div className="play-circle" style={{width: '100px', height: '100px', fontSize: '2.5rem'}}>
                                <i className="fa fa-play"></i>
                            </div>
                        </div>
                        <div className="trending-overlay">
                            <span className="badge bg-danger mb-3 px-3 py-2">TRENDING NOW</span>
                            <h2 className="text-white fw-bold display-5 mb-2">{featuredVideo.title}</h2>
                            <p className="text-white opacity-75 lead mb-3 d-none d-md-block">{featuredVideo.desc}</p>
                            <div className="d-flex align-items-center text-white small">
                                <span className="me-3"><i className="far fa-clock me-2"></i>{featuredVideo.duration}</span>
                                <span><i className="far fa-eye me-2"></i>{featuredVideo.views}</span>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-end mb-5 wow fadeInUp" data-wow-delay="0.2s">
                        <div>
                            <h3 className="fw-bold mb-1">Latest Uploads</h3>
                            <p className="text-muted mb-0">Fresh content from across the nation</p>
                        </div>
                    </div>
                    
                    {/* Video Grid */}
                    <div className="row g-4">
                        {videos.map((video, index) => (
                            <div className="col-md-6 col-lg-3 wow fadeInUp" data-wow-delay={`${0.1 * index}s`} key={index}>
                                <div className="cinematic-card" onClick={() => handlePlayClick(video)}>
                                    <div className="thumbnail-wrapper">
                                        {playingVideoId === video.id && videoUrl ? (
                                            <video 
                                                key={videoUrl}
                                                controls 
                                                autoPlay 
                                                playsInline
                                                className="inline-video-player"
                                                style={{width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0, background: 'black'}}
                                            >
                                                <source src={videoUrl} type="video/mp4" />
                                                <source src={videoUrl} type="video/quicktime" />
                                                <source src={videoUrl} />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : (
                                            <>
                                                <img src={video.thumbnail} alt={video.title} onError={(e) => {e.target.onerror = null; e.target.src = img1}} />
                                                <div className="play-overlay">
                                                    <div className="play-circle">
                                                        <i className="fa fa-play"></i>
                                                    </div>
                                                </div>
                                                <span className="position-absolute bottom-0 end-0 bg-dark text-white px-2 py-1 m-2 rounded small" style={{fontSize: '0.75rem', opacity: '0.9'}}>
                                                    {video.duration}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <div className="video-info">
                                        <div className="video-meta">
                                            <span className="text-primary fw-bold">{video.category}</span>
                                            <span>{video.views}</span>
                                        </div>
                                        <h5 className="video-title">{video.title}</h5>
                                        <div className="mt-auto pt-2 d-flex justify-content-between align-items-center">
                                            <small className="text-muted">{video.date}</small>
                                            <i className="fa fa-ellipsis-v text-muted"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Footer />
            <BackToTop />
        </div>
    );
}
